/**
 * 售后域 API 封装(W-14 取消/退款 + W-13 卖家响应)。复用基座 `call<T>()`。
 * 类型移植 rn-app `services/cancellationService.ts`。
 *
 * 端点:
 *  - POST /api/v1/aftersales/cancellations/{orderId}          → 买家发起取消(W-14)
 *  - PUT  /api/v1/aftersales/cancellations/{orderId}/agree    → 卖家同意(W-13)
 *  - PUT  /api/v1/aftersales/cancellations/{orderId}/reject   → 卖家拒绝→仲裁(W-13)
 */
import { call } from './client';
import type { components } from './openapi.generated';

type Schemas = components['schemas'];

export type CancellationReasonCode =
  | 'SELLER_BREACH'
  | 'NOT_AS_DESCRIBED'
  | 'CHANGED_MIND'
  | 'OTHER';

/** 取消原因 label(买家 W-14 选 + 卖家 W-13 看 共用)。来源 epics.md。 */
export const REASON_LABELS: Record<CancellationReasonCode, string> = {
  SELLER_BREACH: '卖家未履约',
  NOT_AS_DESCRIBED: '与描述不符',
  CHANGED_MIND: '我不想要了',
  OTHER: '其他',
};

/** 下拉渲染顺序。 */
export const REASON_OPTIONS: ReadonlyArray<{ code: CancellationReasonCode; label: string }> = [
  { code: 'SELLER_BREACH', label: REASON_LABELS.SELLER_BREACH },
  { code: 'NOT_AS_DESCRIBED', label: REASON_LABELS.NOT_AS_DESCRIBED },
  { code: 'CHANGED_MIND', label: REASON_LABELS.CHANGED_MIND },
  { code: 'OTHER', label: REASON_LABELS.OTHER },
];

export interface CancellationCreateRequest {
  reasonCode: CancellationReasonCode;
  description: string;
  /** 0–3 张 OSS URL;MVP 默认 []。 */
  evidenceUrls?: string[];
}
export type CancellationCreateResponse = Schemas['CancellationCreateResponse'];

export function requestCancellation(
  orderId: string,
  req: CancellationCreateRequest,
): Promise<CancellationCreateResponse> {
  return call<CancellationCreateResponse>({
    method: 'POST',
    url: `/api/v1/aftersales/cancellations/${encodeURIComponent(orderId)}`,
    data: req,
  });
}

/* ============== 卖家响应(W-13) ============== */
export interface CancellationResolveResponse {
  cancellationId: string;
  orderId: string;
  cancellationState: 'SELLER_AGREED' | 'SELLER_REJECTED';
  orderState: 'CANCELLED' | 'IN_PROGRESS';
  orderSubState: 'NONE' | 'IN_ARBITRATION';
  resolvedAt: string;
  resolution: 'SELLER_AGREED' | 'SELLER_REJECTED';
}

export function agreeCancellation(orderId: string): Promise<CancellationResolveResponse> {
  return call<CancellationResolveResponse>({
    method: 'PUT',
    url: `/api/v1/aftersales/cancellations/${encodeURIComponent(orderId)}/agree`,
  });
}

export function rejectCancellation(
  orderId: string,
  rebuttalText: string,
): Promise<CancellationResolveResponse> {
  return call<CancellationResolveResponse>({
    method: 'PUT',
    url: `/api/v1/aftersales/cancellations/${encodeURIComponent(orderId)}/reject`,
    data: { rebuttalText } satisfies Schemas['CancellationRejectRequest'],
  });
}
